import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

/**
 * Phase TENANT-SUSPEND (P0 security 2026-07-09) — tenant `api` response
 * interceptor for the `TENANT_SUSPENDED` code.  Fires when any protected
 * endpoint (login, refresh, /auth/me, or ANY /api/* call from an
 * existing session) rejects the caller because the parent organisation
 * was suspended by a Super Admin.
 *
 * Behaviour (mirrors the tight scope of the platform interceptor):
 *   - only on 403 whose payload carries `code === "TENANT_SUSPENDED"`
 *   - stash a one-shot flag in sessionStorage so the /login page can
 *     render the friendly explanation
 *   - hard-redirect to `/login?reason=tenant_suspended` — but ONLY if
 *     we're not already there (loop-proof)
 *   - never intercepts platform routes (`/platform/*`) so the Super
 *     Admin who just did the suspend keeps their session
 */
function _extractCode(data) {
  if (!data) return null;
  if (Array.isArray(data.errors) && data.errors[0]) return data.errors[0].code;
  if (typeof data.code === "string") return data.code;
  const d = data.detail;
  if (d && typeof d === "object" && typeof d.code === "string") return d.code;
  return null;
}

api.interceptors.response.use(
  (r) => r,
  (err) => {
    try {
      const status = err?.response?.status;
      const code = _extractCode(err?.response?.data);
      if (status === 403 && code === "TENANT_SUSPENDED"
          && typeof window !== "undefined") {
        const path = window.location.pathname || "";
        if (!path.startsWith("/platform")) {
          try { sessionStorage.setItem("tenantSuspendedNotice", "1"); } catch (e) { /* ignore */ }
          if (!path.startsWith("/login")) {
            window.location.assign("/login?reason=tenant_suspended");
          }
        }
      }
      // 2026-08-25 · 401 session-expired redirect.
      // Any protected `/api/*` call that returns 401 while the user is
      // NOT on the login/setup/platform routes is treated as a session
      // expiry. Ignore probes (`/auth/me`, `/auth/login`, `/auth/logout`)
      // so anonymous first-visit `/auth/me` doesn't bounce the user off
      // a legitimate landing page.
      if (status === 401 && typeof window !== "undefined") {
        const url  = (err?.config?.url || "");
        const path = window.location.pathname || "";
        const isAuthProbe = url.includes("/auth/me")
                         || url.includes("/auth/login")
                         || url.includes("/auth/logout");
        const isOnLogin = path.startsWith("/login")
                       || path.startsWith("/platform")
                       || path.startsWith("/tenant-setup")
                       || path.startsWith("/privacy")
                       || path.startsWith("/terms")
                       || path.startsWith("/pricing")
                       || path.startsWith("/register")
                       || path.startsWith("/hr-login");
        if (!isAuthProbe && !isOnLogin) {
          try { sessionStorage.setItem("sessionExpiredNotice", "1"); } catch (e) { /* ignore */ }
          window.location.assign("/login?reason=session_expired");
        }
      }
    } catch (e) { /* never let the interceptor itself throw */ }
    return Promise.reject(err);
  },
);

// Phase ERRSURFACE — read structured backend validation envelopes so
// real reasons (CUSTOMER_BILL_TO_INCOMPLETE, MISSING_DEFAULT_BANK_FOR_CURRENCY,
// INVOICE_PAYMENT_TERMS_CONFLICT, INVOICE_HAS_NO_LINES, …) surface in
// toasts instead of the generic "Request failed" string. Order of
// preference:
//   1. CertOpz envelope `errors[0]` (code / message / details.missing_fields / conflict_values)
//   2. Raw HTTPException `detail.{code, message, missing_fields}`
//   3. Pydantic `detail` array (`[{loc, msg, …}, …]`)
//   4. Plain string detail
//   5. "Something went wrong." final fallback
function _formatDetailsTail(details) {
  if (!details || typeof details !== "object") return "";
  const tails = [];
  if (Array.isArray(details.missing_fields) && details.missing_fields.length) {
    tails.push(`Missing: ${details.missing_fields.join(", ")}`);
  }
  if (Array.isArray(details.conflict_values) && details.conflict_values.length) {
    tails.push(`Conflicting values: ${details.conflict_values.join(" · ")}`);
  }
  return tails.length ? ` ${tails.join(". ")}` : "";
}

export function formatErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  // CertOpz envelope: { success:false, errors:[{code, message, details}] }
  if (detail && Array.isArray(detail.errors) && detail.errors.length) {
    const e = detail.errors[0] || {};
    const head = (e.message && e.message !== "Request failed")
      ? e.message
      : (e.code || "Request failed");
    return `${head}${_formatDetailsTail(e.details)}`.trim();
  }
  // Raw HTTPException-dict shape (router raised `detail={...}`)
  if (detail && typeof detail === "object"
      && (typeof detail.message === "string" || typeof detail.code === "string")) {
    const head = detail.message || detail.code;
    return `${head}${_formatDetailsTail(detail)}`.trim();
  }
  // Pydantic validation array
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function aedFmt(n) {
  if (n == null || isNaN(n)) return "—";
  return new Intl.NumberFormat("en-AE", { style: "currency", currency: "AED", maximumFractionDigits: 0 }).format(n);
}

// CUSTOMER-CURRENCY-1 — currency-aware money formatter used across the
// commercial UI (quotations, invoices, receipts, case finance, finance
// KPIs). Falls back to AED when currency is missing so legacy rows
// without a stored currency still render correctly. Format: `CUR N,NNN.NN`
// (three-letter ISO prefix + thousands-separated amount). Kept
// intentionally locale-neutral to match the PDF renderer output.
export function moneyFmt(amount, currency, opts = {}) {
  const cur = String(currency || "AED").toUpperCase();
  if (amount == null || isNaN(amount)) return `${cur} —`;
  const decimals = opts.maximumFractionDigits ?? 2;
  const formatted = Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: opts.minimumFractionDigits ?? decimals,
    maximumFractionDigits: decimals,
  });
  return `${cur} ${formatted}`;
}
